/**
 * Occupant-key encoding for per-seat mapping on a table.
 *
 *   "guestId"            → host
 *   "guestId/+1"         → plus-one
 *   "guestId/c/childId"  → child
 *
 * The key is stored in `table.seatMap[seatIndex]`. Helpers here let the
 * rest of the seating feature build and read seatMaps without spreading
 * string-format knowledge everywhere.
 */

import type { Guest, Table } from "@amazing-wedding/shared-types";
import { guestSeatingForPlan, headCount } from "@amazing-wedding/shared-types";
import { getCouplePairForGuest } from "@/features/guests/guest-unit-rows";
import { areCoupleSeatsAdjacent, coupleAdjacentSeatIndices } from "./tableGeometry";

export type PlaceGuestOptions = {
  tableShape?: Table["shape"];
  /** Skip companion keys — used when couple placement defers children until after the partner. */
  hostOnly?: boolean;
  /** Extend companions forward from this seat instead of the host (e.g. after the partner). */
  companionAnchorSeat?: number;
};

export interface OccupantRef {
  guestId: string;
  type: "host" | "plusOne" | "child";
  childId?: string;
}

export function occupantKey(ref: OccupantRef): string {
  switch (ref.type) {
    case "host":
      return ref.guestId;
    case "plusOne":
      return `${ref.guestId}/+1`;
    case "child":
      return `${ref.guestId}/c/${ref.childId}`;
  }
}

const PLUS_ONE_KEY_SUFFIX = "/+1";

export function parseOccupantKey(key: string): OccupantRef {
  if (key.endsWith(PLUS_ONE_KEY_SUFFIX)) {
    return { guestId: key.slice(0, -PLUS_ONE_KEY_SUFFIX.length), type: "plusOne" };
  }
  const cIdx = key.indexOf("/c/");
  if (cIdx >= 0) {
    return { guestId: key.slice(0, cIdx), type: "child", childId: key.slice(cIdx + 3) };
  }
  return { guestId: key, type: "host" };
}

/** Firestore guest doc id — accepts a host id or any seat-map occupant key. */
export function hostGuestDocId(idOrOccupantKey: string): string {
  return parseOccupantKey(idOrOccupantKey).guestId;
}

/**
 * Build a seatMap from a table's guestIds by expanding each guest into
 * host → plusOne → children in order. Used as fallback when the table has
 * no explicit seatMap yet (backward compat with pre-seatMap data).
 */
export function buildSeatMapFromGuestIds(
  table: Pick<Table, "capacity" | "guestIds">,
  guestsById: Record<string, Guest>,
): (string | null)[] {
  const map: (string | null)[] = new Array(table.capacity).fill(null);
  let idx = 0;
  for (const gid of table.guestIds) {
    const g = guestsById[gid];
    if (!g) continue;
    if (idx < table.capacity) map[idx++] = gid;
    if (g.plusOne && idx < table.capacity)
      map[idx++] = occupantKey({ guestId: gid, type: "plusOne" });
    for (const c of g.children ?? []) {
      if (idx >= table.capacity) break;
      map[idx++] = occupantKey({ guestId: gid, type: "child", childId: c.id });
    }
  }
  return map;
}

/**
 * Returns the effective seatMap for a table — uses the persisted one if it
 * has entries, otherwise auto-builds from guestIds.
 *
 * Deduplication: if the same occupant key appears more than once (e.g. due
 * to a race between two concurrent drags), only the first occurrence is kept
 * and the rest are nulled out. This prevents a guest from "ghosting" in two
 * seats simultaneously.
 */
export function effectiveSeatMap(
  table: Table,
  guestsById: Record<string, Guest>,
): (string | null)[] {
  let map: (string | null)[];
  if (table.seatMap.length > 0) {
    map = [...table.seatMap];
    while (map.length < table.capacity) map.push(null);
    map = map.slice(0, table.capacity);
  } else {
    map = buildSeatMapFromGuestIds(table, guestsById);
  }

  // Deduplicate: each occupant key may only appear once.
  const seen = new Set<string>();
  map = map.map((key) => {
    if (key === null) return null;
    if (seen.has(key)) return null;
    seen.add(key);
    return key;
  });

  map = reconcileSeatMapParties(
    map,
    guestsById,
    table.hiddenSeats ?? [],
    table.capacity,
    table.guestIds,
    table.shape,
  );
  return map;
}

/**
 * Re-place every seated invitation unit so all occupant keys (host + companions)
 * exist in the map. Handles corrupt maps where only host OR only companion keys
 * survived the old overwrite bug.
 */
export function reconcileSeatMapParties(
  seatMap: (string | null)[],
  guestsById: Record<string, Guest>,
  hiddenSeats: number[] = [],
  capacity = seatMap.length,
  hostGuestIds: string[] = [],
  tableShape: Table["shape"] = "round",
): (string | null)[] {
  let map = [...seatMap];
  while (map.length < capacity) map.push(null);
  map = map.slice(0, capacity);

  const partyHostIds = [...new Set([...hostGuestIds, ...hostGuestIdsInSeatMap(map)])];

  for (const guestId of partyHostIds) {
    const guest = guestsById[guestId];
    if (!guest) continue;

    const expected = guestOccupantKeys(guest);
    const present = new Set(map.filter((k): k is string => k !== null));
    if (expected.every((k) => present.has(k))) continue;

    const hostSeat = map.findIndex((k) => k === guestId);
    if (hostSeat >= 0) {
      const next = placeGuestInSeatMap(map, guest, hostSeat, hiddenSeats, { tableShape });
      if (expected.every((k) => next.includes(k))) {
        map = next;
        continue;
      }
    }

    map = removeGuestFromSeatMap(map, guestId);
    const slot = findFirstEmptySeatForParty(map, guest, hiddenSeats, { tableShape });
    if (slot == null) continue;

    const next = placeGuestInSeatMap(map, guest, slot, hiddenSeats, { tableShape });
    if (expected.every((k) => next.includes(k))) {
      map = next;
    }
  }

  if (hostGuestIds.length > 0) {
    const present = new Set(map.filter((k): k is string => k !== null));
    const stillBroken = hostGuestIds.some((gid) => {
      const g = guestsById[gid];
      if (!g) return false;
      return !guestOccupantKeys(g).every((k) => present.has(k));
    });
    if (stillBroken) {
      return buildSeatMapFromGuestIds({ capacity, guestIds: hostGuestIds }, guestsById);
    }
  }

  return map;
}

/** @deprecated Use reconcileSeatMapParties */
export function expandIncompletePartiesInSeatMap(
  seatMap: (string | null)[],
  guestsById: Record<string, Guest>,
  hiddenSeats: number[] = [],
): (string | null)[] {
  return reconcileSeatMapParties(seatMap, guestsById, hiddenSeats, seatMap.length);
}

/** True when persisted map is missing companion keys that guest docs require. */
export function seatMapNeedsPartyReconcile(
  table: Pick<Table, "capacity" | "hiddenSeats" | "seatMap" | "guestIds" | "shape">,
  guestsById: Record<string, Guest>,
): boolean {
  const raw = normalizeSeatMap(table);
  const fixed = reconcileSeatMapParties(
    raw,
    guestsById,
    table.hiddenSeats ?? [],
    table.capacity,
    table.guestIds,
    table.shape,
  );
  return !seatMapsEqual(raw, fixed);
}

/** Expected occupied seats from seated invitation units on a table. */
export function expectedFilledSeats(
  table: Pick<Table, "guestIds">,
  guestsById: Record<string, Guest>,
): number {
  return table.guestIds.reduce((sum, gid) => {
    const g = guestsById[gid];
    return sum + (g ? seatingPartySize(g) : 0);
  }, 0);
}

export function normalizeSeatMap(table: Pick<Table, "capacity" | "seatMap">): (string | null)[] {
  let map = table.seatMap.length > 0 ? [...table.seatMap] : [];
  while (map.length < table.capacity) map.push(null);
  return map.slice(0, table.capacity);
}

export function seatMapsEqual(a: (string | null)[], b: (string | null)[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

/**
 * Build all occupant keys for a guest and their companions.
 */
export function guestOccupantKeys(guest: Guest): string[] {
  const keys: string[] = [guest.id];
  if (guest.plusOne) keys.push(occupantKey({ guestId: guest.id, type: "plusOne" }));
  for (const c of guest.children ?? []) {
    keys.push(occupantKey({ guestId: guest.id, type: "child", childId: c.id }));
  }
  return keys;
}

/** Physical seats a party occupies (distinct from RSVP-oriented `headCount`). */
export function seatingPartySize(guest: Guest): number {
  return guestOccupantKeys(guest).length;
}

function isSeatUsable(index: number, seatMap: (string | null)[], hiddenSeats: number[]): boolean {
  const hidden = new Set(hiddenSeats);
  return index >= 0 && index < seatMap.length && !hidden.has(index);
}

/** Next seat when extending a party forward (clockwise on round; along perimeter on rect). */
function partyStepForward(capacity: number, shape: Table["shape"], from: number): number | null {
  if (capacity <= 0) return null;
  if (shape === "round" || shape === "oval") {
    return (from + 1) % capacity;
  }
  const adj = coupleAdjacentSeatIndices(capacity, shape, from);
  const forward = adj.filter((n) => n > from);
  if (forward.length > 0) return forward[0]!;
  return adj.find((n) => n < from) ?? adj[0] ?? null;
}

/** Next seat when extending a party backward from the host anchor. */
function partyStepBackward(capacity: number, shape: Table["shape"], from: number): number | null {
  if (capacity <= 0) return null;
  if (shape === "round" || shape === "oval") {
    return (from - 1 + capacity) % capacity;
  }
  const adj = coupleAdjacentSeatIndices(capacity, shape, from);
  const backward = adj.filter((n) => n < from);
  if (backward.length > 0) return backward[backward.length - 1]!;
  return adj.find((n) => n > from) ?? adj[0] ?? null;
}

function placeCompanionsInArc(
  map: (string | null)[],
  companionKeys: string[],
  hostSeat: number,
  hidden: Set<number>,
  used: Set<number>,
  capacity: number,
  shape: Table["shape"],
  companionAnchorSeat?: number,
): boolean {
  if (companionKeys.length === 0) return true;

  if (companionAnchorSeat !== undefined) {
    let tip = companionAnchorSeat;
    for (const key of companionKeys) {
      const next = partyStepForward(capacity, shape, tip);
      if (next === null || used.has(next) || hidden.has(next) || map[next] !== null) {
        return false;
      }
      map[next] = key;
      used.add(next);
      tip = next;
    }
    return true;
  }

  let rightOpen = true;
  let leftOpen = true;
  let rightTip = hostSeat;
  let leftTip = hostSeat;

  for (const key of companionKeys) {
    let placed = false;

    if (rightOpen) {
      const next = partyStepForward(capacity, shape, rightTip);
      if (next === null || used.has(next) || hidden.has(next) || map[next] !== null) {
        rightOpen = false;
      } else {
        map[next] = key;
        used.add(next);
        rightTip = next;
        placed = true;
      }
    }

    if (!placed && leftOpen) {
      const next = partyStepBackward(capacity, shape, leftTip);
      if (next === null || used.has(next) || hidden.has(next) || map[next] !== null) {
        leftOpen = false;
      } else {
        map[next] = key;
        used.add(next);
        leftTip = next;
        placed = true;
      }
    }

    if (!placed) return false;
  }

  return true;
}

export type PartyDropPlacement = {
  hostSeat: number;
  options: PlaceGuestOptions;
};

function findCoupleAwareCompanionPlacement(
  seatMap: (string | null)[],
  guest: Guest,
  occupantKey: string,
  targetSeat: number,
  hiddenSeats: number[],
  tableShape: Table["shape"],
  allGuests: Guest[],
): PartyDropPlacement | null {
  const ref = parseOccupantKey(occupantKey);
  if (ref.type === "host") return null;

  const pair = getCouplePairForGuest(guest, allGuests);
  if (!pair) return null;

  const partnerId = pair.groom.id === guest.id ? pair.bride.id : pair.groom.id;
  const partnerSeat = partyHostSeatIndex(seatMap, partnerId);
  if (partnerSeat < 0) return null;

  const capacity = seatMap.length;
  if (!areCoupleSeatsAdjacent(capacity, tableShape, partnerSeat, targetSeat)) return null;

  for (const hostSeat of coupleAdjacentSeatIndices(capacity, tableShape, partnerSeat)) {
    if (hostSeat === targetSeat) continue;
    const existing = seatMap[hostSeat];
    if (existing != null && parseOccupantKey(existing).guestId !== guest.id) continue;

    const options: PlaceGuestOptions = {
      tableShape,
      companionAnchorSeat: partnerSeat,
    };
    if (!canPlaceParty(seatMap, guest, hostSeat, hiddenSeats, options)) continue;
    const placed = placeGuestInSeatMap(seatMap, guest, hostSeat, hiddenSeats, options);
    if (placed[targetSeat] === occupantKey) {
      return { hostSeat, options };
    }
  }
  return null;
}

export function resolvePartyDropPlacement(
  seatMap: (string | null)[],
  guest: Guest,
  targetSeat: number,
  hiddenSeats: number[],
  anchorOccupantKey: string | null | undefined,
  tableShape: Table["shape"],
  allGuests?: Guest[],
): PartyDropPlacement | null {
  const options: PlaceGuestOptions = { tableShape };

  if (!anchorOccupantKey || anchorOccupantKey === guest.id) {
    if (!canPlaceParty(seatMap, guest, targetSeat, hiddenSeats, options)) return null;
    return { hostSeat: targetSeat, options };
  }

  if (allGuests) {
    const coupleAware = findCoupleAwareCompanionPlacement(
      seatMap,
      guest,
      anchorOccupantKey,
      targetSeat,
      hiddenSeats,
      tableShape,
      allGuests,
    );
    if (coupleAware) return coupleAware;
  }

  const hostSeat = findHostSeatForPartyOccupantAtSeat(
    seatMap,
    guest,
    anchorOccupantKey,
    targetSeat,
    hiddenSeats,
    options,
  );
  if (hostSeat === null) return null;
  return { hostSeat, options };
}

/** True when every occupant key for the party fits starting at targetSeat. */
export function canPlaceParty(
  seatMap: (string | null)[],
  guest: Guest,
  targetSeat: number,
  hiddenSeats: number[] = [],
  options: PlaceGuestOptions = {},
): boolean {
  if (!isSeatUsable(targetSeat, seatMap, hiddenSeats)) return false;

  const existing = seatMap[targetSeat] ?? null;
  if (existing !== null) {
    const existingRef = parseOccupantKey(existing);
    if (existingRef.guestId !== guest.id) return false;
  }

  const result = placeGuestInSeatMap(seatMap, guest, targetSeat, hiddenSeats, options);
  const keys = guestOccupantKeys(guest);
  return keys.every((key) => result.includes(key));
}

/**
 * Host seat index that places `occupantKey` at `targetSeat`, or null when no fit exists.
 * Scans every viable host anchor — used when a companion pip is dropped onto a seat.
 */
export function findHostSeatForPartyOccupantAtSeat(
  seatMap: (string | null)[],
  guest: Guest,
  occupantKey: string,
  targetSeat: number,
  hiddenSeats: number[] = [],
  options: PlaceGuestOptions = {},
): number | null {
  if (!isSeatUsable(targetSeat, seatMap, hiddenSeats)) return null;
  const keys = guestOccupantKeys(guest);
  if (!keys.includes(occupantKey)) return null;

  for (let hostSeat = 0; hostSeat < seatMap.length; hostSeat++) {
    if (!canPlaceParty(seatMap, guest, hostSeat, hiddenSeats, options)) continue;
    const placed = placeGuestInSeatMap(seatMap, guest, hostSeat, hiddenSeats, options);
    if (placed[targetSeat] === occupantKey) return hostSeat;
  }
  return null;
}

/**
 * Resolve the host anchor seat for a party drop.
 * Host drags land the host at `targetSeat`; companion drags land the dragged occupant there.
 */
export function resolvePartyDropHostSeat(
  seatMap: (string | null)[],
  guest: Guest,
  targetSeat: number,
  hiddenSeats: number[],
  anchorOccupantKey: string | null | undefined,
  tableShape: Table["shape"] = "round",
  allGuests?: Guest[],
): number | null {
  return (
    resolvePartyDropPlacement(
      seatMap,
      guest,
      targetSeat,
      hiddenSeats,
      anchorOccupantKey,
      tableShape,
      allGuests,
    )?.hostSeat ?? null
  );
}

/**
 * First seat index where the whole party (host + companions) can be placed.
 */
export function findFirstEmptySeatForParty(
  seatMap: (string | null)[],
  guest: Guest,
  hiddenSeats: number[] = [],
  options: PlaceGuestOptions = {},
): number | null {
  for (let i = 0; i < seatMap.length; i++) {
    if (canPlaceParty(seatMap, guest, i, hiddenSeats, options)) return i;
  }
  return null;
}

/**
 * Place a guest (host + companions) into a seatMap starting at `targetSeat`.
 * The host goes at `targetSeat`; companions occupy a contiguous arc of seats
 * directly adjacent to the host (extending forward first, then backward).
 * Round/oval tables wrap around the perimeter; rect/square use physical edges.
 */
export function placeGuestInSeatMap(
  seatMap: (string | null)[],
  guest: Guest,
  targetSeat: number,
  hiddenSeats: number[] = [],
  options: PlaceGuestOptions = {},
): (string | null)[] {
  const tableShape = options.tableShape ?? "round";
  const hidden = new Set(hiddenSeats);
  const map = [...seatMap];
  const keys = guestOccupantKeys(guest);
  const N = map.length;
  if (N === 0) return seatMap;

  // Clear any existing entries for this guest first
  for (let i = 0; i < N; i++) {
    if (map[i] !== null && parseOccupantKey(map[i]!).guestId === guest.id) {
      map[i] = null;
    }
  }

  // Place host at target seat — remove any other guest displaced here first
  const displaced = map[targetSeat];
  if (displaced != null) {
    const displacedRef = parseOccupantKey(displaced);
    if (displacedRef.guestId !== guest.id) {
      for (let i = 0; i < N; i++) {
        if (map[i] !== null && parseOccupantKey(map[i]!).guestId === displacedRef.guestId) {
          map[i] = null;
        }
      }
    }
  }
  map[targetSeat] = keys[0] ?? null;

  if (options.hostOnly) {
    return map;
  }

  const remaining = keys.slice(1);
  if (remaining.length > 0) {
    const used = new Set<number>([targetSeat]);
    if (
      !placeCompanionsInArc(
        map,
        remaining,
        targetSeat,
        hidden,
        used,
        N,
        tableShape,
        options.companionAnchorSeat,
      )
    ) {
      return seatMap;
    }
  }

  // Reject partial placement — never leave companions unseated
  if (!keys.every((key) => map.includes(key))) {
    return seatMap;
  }

  return map;
}

/**
 * Remove all seatMap entries for a given guest (host + companions).
 */
export function removeGuestFromSeatMap(
  seatMap: (string | null)[],
  guestId: string,
): (string | null)[] {
  return seatMap.map((key) => {
    if (key !== null && parseOccupantKey(key).guestId === guestId) return null;
    return key;
  });
}

/** Remove both canonical couple members from a seat map. */
export function removeCoupleFromSeatMap(
  seatMap: (string | null)[],
  groomId: string,
  brideId: string,
): (string | null)[] {
  return removeGuestFromSeatMap(removeGuestFromSeatMap(seatMap, groomId), brideId);
}

function findAdjacentEmptySeatForCouplePartner(
  seatMap: (string | null)[],
  anchorSeat: number,
  hiddenSeats: number[],
  tableShape: Table["shape"],
): number | null {
  const hidden = new Set(hiddenSeats);
  for (const seat of coupleAdjacentSeatIndices(seatMap.length, tableShape, anchorSeat)) {
    if (!hidden.has(seat) && seatMap[seat] === null) return seat;
  }
  return null;
}

/**
 * Place groom at `targetSeat`, then bride at the nearest adjacent empty seat.
 * Returns null when either party cannot be fully seated.
 */
export function placeCoupleInSeatMap(
  seatMap: (string | null)[],
  groom: Guest,
  bride: Guest,
  targetSeat: number,
  hiddenSeats: number[] = [],
  tableShape: Table["shape"] = "round",
): (string | null)[] | null {
  if (!isSeatUsable(targetSeat, seatMap, hiddenSeats)) return null;

  const existing = seatMap[targetSeat] ?? null;
  if (existing !== null) {
    const existingRef = parseOccupantKey(existing);
    const isSameGroomHost = existingRef.guestId === groom.id && existingRef.type === "host";
    if (!isSameGroomHost) return null;
  }

  let map = removeCoupleFromSeatMap(seatMap, groom.id, bride.id);
  map = placeGuestInSeatMap(map, groom, targetSeat, hiddenSeats, {
    tableShape,
    hostOnly: true,
  });
  if (partyHostSeatIndex(map, groom.id) < 0) return null;

  const groomHostSeat = partyHostSeatIndex(map, groom.id);

  const brideSeat = findAdjacentEmptySeatForCouplePartner(
    map,
    groomHostSeat,
    hiddenSeats,
    tableShape,
  );
  if (brideSeat == null) return null;

  map = placeGuestInSeatMap(map, bride, brideSeat, hiddenSeats, { tableShape });
  if (!guestOccupantKeys(bride).every((key) => map.includes(key))) return null;
  const brideHostSeat = partyHostSeatIndex(map, bride.id);
  if (
    brideHostSeat < 0 ||
    !areCoupleSeatsAdjacent(map.length, tableShape, groomHostSeat, brideHostSeat)
  ) {
    return null;
  }

  const groomCompanionKeys = guestOccupantKeys(groom).slice(1);
  if (groomCompanionKeys.length > 0) {
    const hidden = new Set(hiddenSeats);
    const used = new Set<number>();
    for (let i = 0; i < map.length; i++) {
      if (map[i]) used.add(i);
    }
    if (
      !placeCompanionsInArc(
        map,
        groomCompanionKeys,
        groomHostSeat,
        hidden,
        used,
        map.length,
        tableShape,
        brideHostSeat,
      )
    ) {
      return null;
    }
    if (!guestOccupantKeys(groom).every((key) => map.includes(key))) return null;
  }

  return map;
}

/** True when groom + bride both fit with groom at `targetSeat`. */
export function canPlaceCoupleParty(
  seatMap: (string | null)[],
  groom: Guest,
  bride: Guest,
  targetSeat: number,
  hiddenSeats: number[] = [],
  tableShape: Table["shape"] = "round",
): boolean {
  return placeCoupleInSeatMap(seatMap, groom, bride, targetSeat, hiddenSeats, tableShape) !== null;
}

/** True when the seatMap key is an anonymous plus-one placeholder (blank name, no guestId). */
export function isAnonymousPlusOneKey(
  key: string | null | undefined,
  guestsById: Record<string, Guest>,
): boolean {
  if (!key) return false;
  const ref = parseOccupantKey(key);
  if (ref.type !== "plusOne") return false;
  const host = guestsById[ref.guestId];
  if (!host?.plusOne) return false;
  return !host.plusOne.firstName?.trim() && !host.plusOne.guestId?.trim();
}

export function partyHasNonHostOccupants(seatMap: (string | null)[], guestId: string): boolean {
  return seatMap.some((key) => {
    if (!key) return false;
    const ref = parseOccupantKey(key);
    return ref.guestId === guestId && ref.type !== "host";
  });
}

/** True when a solo host guest can swap seats with an anonymous plus-one occupant slot. */
export function canSwapSoloGuestWithAnonymousPlusOne(params: {
  targetKey: string | null | undefined;
  sourceKey: string | null | undefined;
  moverGuest: Guest;
  moverGuestId: string;
  guestsById: Record<string, Guest>;
  moverHasCompanionInMap?: boolean;
}): boolean {
  const {
    targetKey,
    sourceKey,
    moverGuest,
    moverGuestId,
    guestsById,
    moverHasCompanionInMap = false,
  } = params;
  if (!targetKey || !sourceKey) return false;
  if (!isAnonymousPlusOneKey(targetKey, guestsById)) return false;
  if (headCount(moverGuest) !== 1 || moverHasCompanionInMap) return false;
  const sourceRef = parseOccupantKey(sourceKey);
  return sourceRef.guestId === moverGuestId && sourceRef.type === "host";
}

export function swapSeatOccupants(
  seatMap: (string | null)[],
  sourceSeatIndex: number,
  targetSeatIndex: number,
): (string | null)[] {
  const newMap = [...seatMap];
  newMap[sourceSeatIndex] = seatMap[targetSeatIndex] ?? null;
  newMap[targetSeatIndex] = seatMap[sourceSeatIndex] ?? null;
  return newMap;
}

/** Sorted seat indices occupied by a host's invitation unit (host + companions). */
export function partySeatIndices(seatMap: (string | null)[], guestId: string): number[] {
  const indices: number[] = [];
  seatMap.forEach((key, i) => {
    if (key && parseOccupantKey(key).guestId === guestId) indices.push(i);
  });
  return indices.sort((a, b) => a - b);
}

/** Index of the host key for a seated party, or -1. */
export function partyHostSeatIndex(seatMap: (string | null)[], guestId: string): number {
  return seatMap.findIndex((key) => {
    if (!key) return false;
    const ref = parseOccupantKey(key);
    return ref.guestId === guestId && ref.type === "host";
  });
}

/** True when two parties are fully seated and have equal physical size. */
export function canSwapParties(seatMap: (string | null)[], guestA: Guest, guestB: Guest): boolean {
  if (guestA.id === guestB.id) return false;
  const indicesA = partySeatIndices(seatMap, guestA.id);
  const indicesB = partySeatIndices(seatMap, guestB.id);
  const sizeA = seatingPartySize(guestA);
  const sizeB = seatingPartySize(guestB);
  return indicesA.length === sizeA && indicesB.length === sizeB && sizeA === sizeB;
}

/**
 * Exchange occupant keys between two equal-size parties. Returns null when invalid.
 */
export function swapPartiesInSeatMap(
  seatMap: (string | null)[],
  guestAId: string,
  guestBId: string,
): (string | null)[] | null {
  const indicesA = partySeatIndices(seatMap, guestAId);
  const indicesB = partySeatIndices(seatMap, guestBId);
  if (indicesA.length === 0 || indicesB.length === 0 || indicesA.length !== indicesB.length) {
    return null;
  }

  const keysA = indicesA.map((i) => seatMap[i]!);
  const keysB = indicesB.map((i) => seatMap[i]!);
  const newMap = [...seatMap];

  for (const i of [...indicesA, ...indicesB]) newMap[i] = null;
  for (let k = 0; k < indicesA.length; k++) {
    newMap[indicesB[k]!] = keysA[k] ?? null;
    newMap[indicesA[k]!] = keysB[k] ?? null;
  }

  return newMap;
}

export type RelocatePartiesResult = {
  seatMap: (string | null)[];
  moverHostSeat: number;
  displacedHostSeat: number;
};

/** Simulate mutual relocate: mover → target host seat, displaced → mover's former host seat. */
export function canRelocateParties(
  seatMap: (string | null)[],
  mover: Guest,
  displaced: Guest,
  targetSeat: number,
  hiddenSeats: number[] = [],
): boolean {
  return tryRelocatePartiesInSeatMap(seatMap, mover, displaced, targetSeat, hiddenSeats) !== null;
}

/**
 * Remove both parties, place mover at targetSeat, displaced at mover's old host seat.
 * Returns null when either party cannot be fully placed.
 */
export function tryRelocatePartiesInSeatMap(
  seatMap: (string | null)[],
  mover: Guest,
  displaced: Guest,
  targetSeat: number,
  hiddenSeats: number[] = [],
): RelocatePartiesResult | null {
  if (mover.id === displaced.id) return null;

  const moverHostSeat = partyHostSeatIndex(seatMap, mover.id);
  if (moverHostSeat < 0) return null;

  const withoutBoth = removeGuestFromSeatMap(
    removeGuestFromSeatMap(seatMap, mover.id),
    displaced.id,
  );

  const afterMover = placeGuestInSeatMap(withoutBoth, mover, targetSeat, hiddenSeats);
  if (!guestOccupantKeys(mover).every((k) => afterMover.includes(k))) return null;

  const afterBoth = placeGuestInSeatMap(afterMover, displaced, moverHostSeat, hiddenSeats);
  if (!guestOccupantKeys(displaced).every((k) => afterBoth.includes(k))) return null;

  return {
    seatMap: afterBoth,
    moverHostSeat: targetSeat,
    displacedHostSeat: moverHostSeat,
  };
}

export type WithinTableOccupiedMoveResult = {
  seatMap: (string | null)[];
  guestSeatingUpdates: Array<{ guestId: string; seatIndex: number }>;
};

/** Swap two seated members of one invitation unit (host ↔ companion). */
export function swapPartyMemberSeatsInSeatMap(
  seatMap: (string | null)[],
  guestId: string,
): WithinTableOccupiedMoveResult | null {
  const indices = partySeatIndices(seatMap, guestId);
  if (indices.length !== 2) return null;
  const seatA = indices[0];
  const seatB = indices[1];
  if (seatA === undefined || seatB === undefined) return null;
  const newMap = swapSeatOccupants(seatMap, seatA, seatB);
  const hostSeat = partyHostSeatIndex(newMap, guestId);
  if (hostSeat < 0) return null;
  return {
    seatMap: newMap,
    guestSeatingUpdates: [{ guestId: hostGuestDocId(guestId), seatIndex: hostSeat }],
  };
}

/** Swap groom and bride host seats on the same table. */
export function swapCoupleMemberSeatsInSeatMap(
  seatMap: (string | null)[],
  groomId: string,
  brideId: string,
): WithinTableOccupiedMoveResult | null {
  const groomSeat = partyHostSeatIndex(seatMap, groomId);
  const brideSeat = partyHostSeatIndex(seatMap, brideId);
  if (groomSeat < 0 || brideSeat < 0 || groomSeat === brideSeat) return null;
  const newMap = swapSeatOccupants(seatMap, groomSeat, brideSeat);
  const groomHostAfter = partyHostSeatIndex(newMap, groomId);
  const brideHostAfter = partyHostSeatIndex(newMap, brideId);
  if (groomHostAfter < 0 || brideHostAfter < 0) return null;
  return {
    seatMap: newMap,
    guestSeatingUpdates: [
      { guestId: groomId, seatIndex: groomHostAfter },
      { guestId: brideId, seatIndex: brideHostAfter },
    ],
  };
}

/**
 * Resolve a within-table drop onto an occupied seat without evicting the displaced party.
 * Returns null when no swap/relocate applies.
 */
export function applyWithinTableOccupiedMove(params: {
  seatMap: (string | null)[];
  mover: Guest;
  moverGuestId: string;
  sourceSeatIndex: number;
  targetSeatIndex: number;
  hiddenSeats: number[];
  guestsById: Record<string, Guest>;
}): WithinTableOccupiedMoveResult | null {
  const {
    seatMap,
    mover,
    moverGuestId,
    sourceSeatIndex,
    targetSeatIndex,
    hiddenSeats,
    guestsById,
  } = params;

  const targetKey = seatMap[targetSeatIndex];
  if (!targetKey) return null;

  const displacedGuestId = parseOccupantKey(targetKey).guestId;
  if (displacedGuestId === moverGuestId) return null;

  const displaced = guestsById[displacedGuestId];
  if (!displaced) return null;

  const sourceKey = seatMap[sourceSeatIndex] ?? null;
  if (
    canSwapSoloGuestWithAnonymousPlusOne({
      targetKey,
      sourceKey,
      moverGuest: mover,
      moverGuestId,
      guestsById,
      moverHasCompanionInMap: partyHasNonHostOccupants(seatMap, moverGuestId),
    })
  ) {
    return {
      seatMap: swapSeatOccupants(seatMap, sourceSeatIndex, targetSeatIndex),
      guestSeatingUpdates: [{ guestId: hostGuestDocId(moverGuestId), seatIndex: targetSeatIndex }],
    };
  }

  const displacedHasCompanion = partyHasNonHostOccupants(seatMap, displacedGuestId);
  const moverHasCompanion = partyHasNonHostOccupants(seatMap, moverGuestId);
  const canSoloSwap = headCount(mover) === 1 && !moverHasCompanion && !displacedHasCompanion;

  if (canSoloSwap) {
    return {
      seatMap: swapSeatOccupants(seatMap, sourceSeatIndex, targetSeatIndex),
      guestSeatingUpdates: [{ guestId: hostGuestDocId(moverGuestId), seatIndex: targetSeatIndex }],
    };
  }

  if (canSwapParties(seatMap, mover, displaced)) {
    const swapped = swapPartiesInSeatMap(seatMap, moverGuestId, displacedGuestId);
    if (!swapped) return null;
    const moverNewHost = partyHostSeatIndex(swapped, moverGuestId);
    const displacedNewHost = partyHostSeatIndex(swapped, displacedGuestId);
    if (moverNewHost < 0 || displacedNewHost < 0) return null;
    return {
      seatMap: swapped,
      guestSeatingUpdates: [
        { guestId: hostGuestDocId(moverGuestId), seatIndex: moverNewHost },
        { guestId: hostGuestDocId(displacedGuestId), seatIndex: displacedNewHost },
      ],
    };
  }

  const relocated = tryRelocatePartiesInSeatMap(
    seatMap,
    mover,
    displaced,
    targetSeatIndex,
    hiddenSeats,
  );
  if (!relocated) return null;

  return {
    seatMap: relocated.seatMap,
    guestSeatingUpdates: [
      { guestId: hostGuestDocId(moverGuestId), seatIndex: relocated.moverHostSeat },
      { guestId: hostGuestDocId(displacedGuestId), seatIndex: relocated.displacedHostSeat },
    ],
  };
}

/** Pre-flight check for a within-table drag drop. */
export function canMoveWithinTable(params: {
  seatMap: (string | null)[];
  guest: Guest;
  guestId: string;
  sourceSeatIndex: number;
  targetSeatIndex: number;
  hiddenSeats: number[];
  guestsById: Record<string, Guest>;
  /** Occupant key on the dragged pip; defaults to the host id. */
  anchorOccupantKey?: string | null;
  tableShape?: Table["shape"];
  allGuests?: Guest[];
}): boolean {
  const {
    seatMap,
    guest,
    guestId,
    sourceSeatIndex,
    targetSeatIndex,
    hiddenSeats,
    guestsById,
    anchorOccupantKey,
    tableShape = "round",
    allGuests,
  } = params;

  if (sourceSeatIndex === targetSeatIndex) return false;

  if (!seatMap[targetSeatIndex]) {
    const withoutSelf = removeGuestFromSeatMap(seatMap, guestId);
    return (
      resolvePartyDropPlacement(
        withoutSelf,
        guest,
        targetSeatIndex,
        hiddenSeats,
        anchorOccupantKey ?? guestId,
        tableShape,
        allGuests,
      ) !== null
    );
  }

  const hostSource =
    partyHostSeatIndex(seatMap, guestId) >= 0
      ? partyHostSeatIndex(seatMap, guestId)
      : sourceSeatIndex;

  return (
    applyWithinTableOccupiedMove({
      seatMap,
      mover: guest,
      moverGuestId: guestId,
      sourceSeatIndex: hostSource,
      targetSeatIndex,
      hiddenSeats,
      guestsById,
    }) !== null
  );
}

/** Usable seats on a table (capacity minus deactivated/hidden indices). */
export function usableCapacity(table: Pick<Table, "capacity" | "hiddenSeats">): number {
  return table.capacity - (table.hiddenSeats?.length ?? 0);
}

/** Occupied seats in a seat map, excluding hidden indices. */
export function countOccupiedSeats(
  table: Pick<Table, "capacity" | "hiddenSeats">,
  seatMap: (string | null)[],
): number {
  const hidden = new Set(table.hiddenSeats ?? []);
  let count = 0;
  for (let i = 0; i < table.capacity; i++) {
    if (hidden.has(i)) continue;
    if (seatMap[i]) count++;
  }
  return count;
}

/** Unique host guest IDs seated at a table (companions map to their host). */
export function hostGuestIdsInSeatMap(seatMap: (string | null)[]): string[] {
  const ids = new Set<string>();
  for (const key of seatMap) {
    if (!key) continue;
    const ref = parseOccupantKey(key);
    if (ref.type === "host") ids.add(ref.guestId);
  }
  return [...ids];
}

export function guestAppearsInSeatMap(seatMap: (string | null)[], guestId: string): boolean {
  return partyHostSeatIndex(seatMap, guestId) >= 0;
}

/** Remove host parties whose authoritative plan seating points at another table. */
export function stripCrossTableOrphanHosts(
  seatMap: (string | null)[],
  tableId: string,
  guestsById: Record<string, Guest>,
  planId: string,
  legacyPlanId: string | null | undefined,
): (string | null)[] {
  let map = [...seatMap];
  for (const key of seatMap) {
    if (!key) continue;
    const ref = parseOccupantKey(key);
    if (ref.type !== "host") continue;
    const guest = guestsById[ref.guestId];
    if (!guest) continue;
    const seatedAt = guestSeatingForPlan(guest, planId, legacyPlanId).tableId;
    if (seatedAt && seatedAt !== tableId) {
      map = removeGuestFromSeatMap(map, ref.guestId);
    }
  }
  return map;
}
